/* eslint-disable multiline-ternary */

import { FC, HTMLAttributes, ReactNode, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Typography from 'src/components/common/typography/Typography';

import './profilePicture.scss';
import Skeleton from 'src/components/common/skeleton/Skeleton';

export type TProfilePicture = {
  url: string;
  fallback: ReactNode;
  userType?: string;
} & HTMLAttributes<HTMLDivElement>;

/*
 ** Component to representative text incase the image is not available or failed loading
 */
const ProfilePicture: FC<TProfilePicture> = ({
  url = '',
  fallback,
  className,
  userType,
  ...rest
}: TProfilePicture) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isImageValid, setIsImageValid] = useState<boolean>(true);

  useEffect(() => {
    if (!url) {
      setIsImageValid(false);
      setIsLoading(false);
      return;
    }

    // Prevent unnecessary reloading
    const img = new Image();
    img.src = url;

    if (img.complete) {
      setIsImageValid(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    img.onload = () => {
      setIsImageValid(true);
      setIsLoading(false);
    };

    img.onerror = () => {
      setIsImageValid(false);
      setIsLoading(false);
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [url]);
  const profilePictureUrl = useMemo(() => {
      return url ? `${url}?t=${Date.now()}` : '';
    }, [url]);
  return (
    <div
      className={classNames('profile-picture', `profile-picture--${userType}`, className)}
      {...rest}
    >
      {isImageValid ? (
        <img key={profilePictureUrl} src={profilePictureUrl} style={isLoading ? { display: 'none' } : { display: 'block' }} />
      ) : (
        <Typography
          as="h3"
          variant="body-1"
          fontWeight="semiBold"
          className="profile-picture-component__error"
          style={isLoading ? { display: 'none' } : { display: 'block' }}
        >
          {fallback}
        </Typography>
      )}
      {isLoading && <Skeleton />}
    </div>
  );
};

ProfilePicture.defaultProps = {
  url: ''
};

export default ProfilePicture;
